/**
 * S-33 Safety Center (V1 auth-profiles-gdpr — additive file in the safety
 * dir; V8's blocklist/hidden-words/banned files are untouched).
 *
 * Static content page, offline-cached with the PWA shell. ZERO jokes on this
 * page (copy-tone §1.4): meeting-safely checklist, video-date-first advice,
 * emergency numbers note (EU 112 + the honest no-integration statement),
 * reporting guide, blocking guide, data-privacy summary, contact.
 */
import { Link } from "react-router-dom";
import { Card, IconBrokenHeart } from "../../design/index.js";
import "./safety.css";

const SUPPORT_EMAIL = "support@humble.ekaii.fr";

export function SafetyCenterScreen() {
  return (
    <div data-screen="S-33" className="safety-page">
      <nav className="safety-nav">
        <Link to="/" className="safety-nav__brand">
          <IconBrokenHeart width={20} height={20} style={{ color: "var(--accent)" }} />
          humble
        </Link>
        <Link to="/" style={{ fontSize: "0.9rem" }}>
          Back
        </Link>
      </nav>

      <h1>Safety Center</h1>
      <p>
        This page is serious, and so are we about it. Nothing here jokes, nothing here is for
        sale, and none of it can be switched off.
      </p>

      <Card padding="lg">
        <h2>Meeting someone for the first time</h2>
        <ul style={{ paddingLeft: 20, margin: 0 }}>
          <li>Meet in a public place — a café, a bar, somewhere with people around.</li>
          <li>Tell a friend where you are going, with whom, and when you expect to be back.</li>
          <li>Arrange your own transport there and back. Keep your phone charged.</li>
          <li>Keep your drink in sight. Order your own.</li>
          <li>
            If anything feels wrong, leave. You owe nobody an explanation, and you never owe
            anyone a second hour.
          </li>
          <li>Do not share your home address, workplace address, or financial details.</li>
        </ul>
      </Card>

      <Card padding="lg">
        <h2>Video-date first</h2>
        <p style={{ margin: 0 }}>
          A short video call before meeting in person is the single most effective safety habit:
          it confirms the person matches their profile and gives you a low-stakes read on how
          they behave. Humble supports voice and video calls inside any match once both of you
          have exchanged a few messages — use them before you commit an evening.
        </p>
      </Card>

      <Card padding="lg">
        <h2>Emergencies</h2>
        <p style={{ margin: 0 }}>
          If you are in immediate danger, call your local emergency number — <strong>112</strong>{" "}
          works throughout the EU (911 in North America). Be honest with yourself about danger
          signals. <strong>Humble has no emergency-services integration</strong>: no button in
          this app calls anyone for you. Your phone's dialer is faster than anything we could
          build, and we would rather tell you that plainly than pretend otherwise.
        </p>
      </Card>

      <Card padding="lg">
        <h2>Reporting</h2>
        <p>
          Every profile, conversation, call and Roulette session has a Report action. Reports
          are reviewed by a human within 24 hours. The person you report is never told who
          reported them. Attach as much context as you can — screenshots of behavior outside
          Humble help too.
        </p>
        <p style={{ margin: 0 }}>
          Report first, block second: reporting preserves the evidence for our team; blocking
          protects you immediately. Do both.
        </p>
      </Card>

      <Card padding="lg">
        <h2>Blocking</h2>
        <p style={{ margin: 0 }}>
          Blocking is instant, silent and total: you disappear from each other's decks, matches,
          messages, calls and Roulette. The blocked person is not notified. Blocking works
          against every account — no purchase, boost or "mode" weakens it, ever. Manage your
          blocklist in Settings › Privacy.
        </p>
      </Card>

      <Card padding="lg">
        <h2>Your data</h2>
        <p style={{ margin: 0 }}>
          Humble stores the minimum it needs: your profile, your matches and messages, coarse
          location (roughly 1 km — never your exact position), and purchase records. You can
          export everything you own as a file, pause your visibility, or delete your account
          (14-day grace period, then permanent erasure) — all from your profile page. Location
          use is consent-based and revocable. Full details:{" "}
          <Link to="/legal/privacy">Privacy Policy</Link>.
        </p>
      </Card>

      <Card padding="lg">
        <h2>Contact</h2>
        <p style={{ margin: 0 }}>
          Safety concerns, questions, or anything this page did not answer:{" "}
          <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>. Account appeals:{" "}
          <a href="mailto:appeal@humble.ekaii.fr">appeal@humble.ekaii.fr</a>.
        </p>
      </Card>
    </div>
  );
}
